/**
 * MSAL browser setup.
 *
 * Authorisation code flow with PKCE, single tenant. Who is permitted to sign in
 * at all is controlled in Entra ID ("Assignment required" on the enterprise
 * application, granted via the SG-Complaints-Users group) — not here.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { config } from '../config';

const msalConfig: Configuration = {
  auth: {
    clientId: config.clientId,
    authority: `https://login.microsoftonline.com/${config.tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage keeps the session to one tab and clears it when the browser
    // closes — appropriate for a shared workstation handling complaint records.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/** Scopes requested at sign-in: identity plus access to our own API. */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', config.apiScope],
};

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Returns a bearer token for our API, refreshing silently when possible and
 * falling back to a redirect when the refresh token has expired or consent
 * is needed.
 */
export async function acquireApiToken(): Promise<string> {
  const account = getActiveAccount();
  if (!account) throw new Error('Not signed in');

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [config.apiScope],
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [config.apiScope], account });
      // The redirect navigates away; this line is never reached.
      throw error;
    }
    throw error;
  }
}

export async function signIn(): Promise<void> {
  await msalInstance.loginRedirect(loginRequest);
}

export async function signOut(): Promise<void> {
  const account = getActiveAccount();
  await msalInstance.logoutRedirect({ account: account ?? undefined });
}
